import type { ReactNode } from 'react';
import { useState } from 'react';

import { AnimatePresence, useReducedMotion } from 'framer-motion';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { segmentTrack } from 'src/app/state/actions';
import type { Dispatch } from 'src/app/state/store';
import type { HomeItem } from 'src/app/types';
import { PATIENT_ACCORDION_BUTTON, PATIENT_ACCORDION_CONTENT } from 'src/consts/testIDs';

import { AnimatedMedications } from '../AnimatedMedications';

import {
  AnnoyingHackToUseCSS,
  PatientAccordionHeader,
  PatientAccordionItem,
  PatientAccordionItemContent,
  PatientAccordionRoot,
  PatientAccordionTriangle,
  PatientAccordionTrigger,
  PatientName,
} from './styles';

const mapDispatchToProps = (dispatch: Dispatch) => ({
  dispatchSegmentTrack: (event: UnifiedEvent, payload: Record<string, any>) => dispatch(segmentTrack(event, payload)),
});

interface PatientAccordionProps {
  items: HomeItem[];
  hasMultiplePatients: boolean;
  showNoActiveMedications: boolean;
}

interface ConnectedPatientAccordionProps extends PatientAccordionProps, ReturnType<typeof mapDispatchToProps> {}

const _PatientAccordion = ({
  items,
  hasMultiplePatients,
  showNoActiveMedications,
  dispatchSegmentTrack,
}: ConnectedPatientAccordionProps) => {
  const shouldReduceMotion = useReducedMotion();
  const allPatientIDs = items.map(({ patient }) => patient.id);
  const [expandedPatientIDs, setExpandedPatientIDs] = useState(allPatientIDs);

  return (
    <AnnoyingHackToUseCSS>
      <PatientAccordionRoot type="multiple" value={expandedPatientIDs} onValueChange={setExpandedPatientIDs}>
        {items?.map(({ patient, items: prescriptionItems }) => {
          const { firstName, lastName } = patient;
          const fullName = `${firstName} ${lastName}`;

          const isExpanded = expandedPatientIDs.includes(patient.id);

          return (
            <PatientAccordionItem
              value={patient.id}
              key={patient.id}
              style={{
                // needed to ensure animation crisply wipes content off screen
                overflow: isExpanded ? 'initial' : 'hidden',
              }}
            >
              <PatientAccordionHeader>
                <PatientAccordionTrigger
                  data-testid={PATIENT_ACCORDION_BUTTON}
                  onClick={() => {
                    dispatchSegmentTrack(
                      isExpanded ? UNIFIED.collapseFamilyMemberMeds : UNIFIED.expandFamilyMemberMeds,
                      {
                        location: 'Rx Tab',
                        patient_id: patient.id,
                        is_primary: patient.isPrimary,
                      }
                    );
                  }}
                >
                  <PatientAccordionTriangle isExpanded={isExpanded} />

                  <PatientName>{fullName}</PatientName>
                </PatientAccordionTrigger>
              </PatientAccordionHeader>

              <AnimatePresence>
                {isExpanded && (
                  <PatientAccordionItemContent
                    data-testid={PATIENT_ACCORDION_CONTENT}
                    forceMount={true}
                    initial="collapsed"
                    animate="open"
                    exit="collapsed"
                    variants={{
                      open: { height: 'auto', opacity: 1, marginTop: '-1.6rem' },
                      collapsed: {
                        height: shouldReduceMotion ? 'auto' : 0,
                        opacity: 0,
                        marginTop: shouldReduceMotion ? '-1.6rem' : 0,
                      },
                    }}
                    transition={{ bounce: 0 }}
                  >
                    <AnimatedMedications
                      prescriptionItems={prescriptionItems}
                      hasMultiplePatients={hasMultiplePatients}
                      showNoActiveMedications={showNoActiveMedications}
                    />
                  </PatientAccordionItemContent>
                )}
              </AnimatePresence>
            </PatientAccordionItem>
          );
        })}
      </PatientAccordionRoot>
    </AnnoyingHackToUseCSS>
  );
};

export const PatientAccordion: (props: PatientAccordionProps) => ReactNode = compose(connect(null, mapDispatchToProps))(
  _PatientAccordion
);
